import { useEffect, useState } from 'react';

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

type NavigatorWithStandalone = Navigator & { standalone?: boolean };

const isStandalone = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches ||
  (navigator as NavigatorWithStandalone).standalone === true;

const isIos = (): boolean =>
  /iPad|iPhone|iPod/u.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

const InstallPrompt = () => {
  const [promptEvent, setPromptEvent] = useState<InstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(isStandalone);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    const capturePrompt = (event: Event) => {
      event.preventDefault();
      setPromptEvent(event as InstallPromptEvent);
    };
    const markInstalled = () => {
      setInstalled(true);
      setPromptEvent(null);
    };
    window.addEventListener('beforeinstallprompt', capturePrompt);
    window.addEventListener('appinstalled', markInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', capturePrompt);
      window.removeEventListener('appinstalled', markInstalled);
    };
  }, []);

  if (installed || (!promptEvent && !isIos())) return null;

  const install = async () => {
    if (!promptEvent) {
      setShowIosHelp(true);
      return;
    }
    await promptEvent.prompt();
    const choice = await promptEvent.userChoice;
    if (choice.outcome === 'accepted') setInstalled(true);
    setPromptEvent(null);
  };

  return (
    <aside className="install-prompt" aria-label="AI MessageBot installeren">
      <div>
        <strong>Installeer AI MessageBot</strong>
        <span>Open sneller als zelfstandige app.</span>
        {showIosHelp && (
          <p>
            Tik in Safari op Deel en kies daarna ‘Zet op beginscherm’.
          </p>
        )}
      </div>
      <button type="button" className="button primary small" onClick={() => void install()}>
        {isIos() ? 'Toon iOS-stappen' : 'Installeren'}
      </button>
      <button
        type="button"
        className="button icon"
        onClick={() => setInstalled(true)}
        aria-label="Installatiemelding sluiten"
      >
        ×
      </button>
    </aside>
  );
};

export default InstallPrompt;
